import React, { useEffect, useState } from 'react'
import {
  useLazyListHRDocumentQuery,
  useLazyActivateHRDocumentQuery,
  useLazyDeactivateHRDocumentQuery,
} from '@/services/hrUpload/hrUploadReducer'
import { useLazyGetJobStatusQuery } from '@/services/application/annotateReducer'
import HRListItem from './HRListItem'
import { useDispatch, useSelector } from 'react-redux'
import { addHRDocument, setBackgroundIndexingJobs, updateAllHRDocumentStatus } from './HRListSlice'
import { useParams } from 'react-router-dom'
import MappingModal from './MappingModal'
import { MotifProgressLoader } from '@ey-xd/motif-react'
import ConfirmationModal from '@/components/Modal/ConfirmationModal/ConfirmationModal'
import { HRDocumentStatusType } from './type'
import { createSelector } from '@reduxjs/toolkit'
import { RootState } from '@/store'
import { EngagementDetail } from '@/services/types'

export const selectHRListByEngagementId = createSelector(
  [
    (state: any) => state.HRList.HRList,
    (_: any, engagementId: string) => engagementId,
  ],
  (HRList, engagementId) =>
    HRList.filter(
      (item: any) =>
        item.engagementId === engagementId,
    )
)

export default function List({ isListUpdated }: any) {
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [showMapping, setShowMapping] = useState(false)
  const [indexingStepInfo, setIndexingStepInfo] = useState<Record<string, { step: number; totalSteps: number; description: string }>>({})
  const [currentDocumentId, setCurrentDocumentId] = useState('')
  const [activationJobId, setActivationJobId] = useState('')
  const [isActivationStarting, setIsActivationStarting] = useState(false)
  const [isActivationPolling, setIsActivationPolling] = useState(false)
  const [activationMessage, setActivationMessage] = useState('You can activate one version at a given time.')
  const [activationFinished, setActivationFinished] = useState(false)
  const [activationFailed, setActivationFailed] = useState(false)
  const { engagementId } = useParams<{ engagementId: string }>()
  const dispatch = useDispatch()
  const listHRDocumentState = useSelector((state: RootState) =>
    selectHRListByEngagementId(state, engagementId ?? '')
  )
  const [listHRDocument, { data: HRList, isLoading }] =
    useLazyListHRDocumentQuery()
  const [activateDocument] = useLazyActivateHRDocumentQuery()
  const [deActivateDocument] = useLazyDeactivateHRDocumentQuery()
  const [getJobStatus] = useLazyGetJobStatusQuery()
  const backgroundIndexingJobs = useSelector((state: any) => state.HRList.backgroundIndexingJobs)
  const isArchived = useSelector(
    (state: { clientCard: { currentEngagement: EngagementDetail } }) =>
      state.clientCard.currentEngagement?.isArchived,
  )

  useEffect(() => {
    listHRDocument(engagementId)
  }, [isListUpdated])

  useEffect(() => {
    if (HRList && HRList.length > 0) {
      HRList.forEach((hrList: any) => {
        const {
          id,
          engagementId,
          displayName,
          mappingCompleted,
          isActive,
          createdOn,
          createdByName,
          documentStatus,
        } = hrList
        const newHRDocument = {
          id,
          engagementId,
          displayName,
          mappingCompleted,
          isActive,
          createdDate: createdOn,
          createdBy: createdByName,
          status: true,
          documentStatus,
        }
        dispatch(addHRDocument(newHRDocument))
      })

      if (HRList.some((h: { documentStatus: HRDocumentStatusType }) => h.documentStatus === HRDocumentStatusType.MalwareScan)) {
        // Stop condition relies on this effect's cleanup + re-run when HRList
        // updates, rather than checking the stale HRList closed over here.
        const timer = setInterval(() => {
          listHRDocument(engagementId)
        }, 30000)

        return () => clearInterval(timer)
      }
    } else if (HRList && HRList.length === 0) {
      dispatch(updateAllHRDocumentStatus([]))
    }
  }, [HRList, engagementId, listHRDocument])

  useEffect(() => {
    const interval = setInterval(() => {
      listHRDocument(engagementId)
    }, 60000)
    return () => clearInterval(interval)
  }, [engagementId, listHRDocument])

  useEffect(() => {
    const jobEntries = Object.entries(backgroundIndexingJobs).filter(
      ([, jobId]) => typeof jobId === 'string' && jobId.trim() !== ''
    )

    if (jobEntries.length === 0) {
      return
    }

    const pollBackgroundIndexing = async () => {
      for (const [documentId, jobId] of jobEntries) {
        try {
          const result = await getJobStatus(jobId).unwrap()

          if (result.currentStep && result.totalSteps) {
            setIndexingStepInfo((prev) => ({
              ...prev,
              [documentId]: {
                step: result.currentStep,
                totalSteps: result.totalSteps,
                description: result.stepDescription || '',
              },
            }))
          }

          if (result.statusId === 1 || result.statusId === 2) {
            dispatch(setBackgroundIndexingJobs({ documentId, jobId: null }))
            setIndexingStepInfo((prev) => {
              const next = { ...prev }
              delete next[documentId]
              return next
            })
            listHRDocument(engagementId)
          }
        } catch (_error) {
          dispatch(setBackgroundIndexingJobs({ documentId, jobId: null }))
        }
      }
    }

    pollBackgroundIndexing()
    const interval = setInterval(pollBackgroundIndexing, 3000)
    return () => clearInterval(interval)
  }, [backgroundIndexingJobs, engagementId, getJobStatus, listHRDocument])

  useEffect(() => {
    if (!showConfirmModal || !activationJobId || activationFinished || activationFailed) {
      return
    }

    setIsActivationPolling(true)

    const pollJobStatus = async () => {
      try {
        const result = await getJobStatus(activationJobId).unwrap()
        if (result.statusId === 1) {
          handleUpdateAllIsActive(currentDocumentId, true)
          setActivationFinished(true)
          setIsActivationPolling(false)
          setActivationMessage('Embeddings finished successfully. You can move ahead now.')
          return
        }

        if (result.statusId === 2) {
          setActivationFailed(true)
          setIsActivationPolling(false)
          setActivationMessage('Embedding job failed. Please close this dialog and try again.')
          return
        }

        setActivationMessage('Embeddings started. Please stay on this screen while indexing completes.')
      } catch (_error) {
        setActivationFailed(true)
        setIsActivationPolling(false)
        setActivationMessage('Unable to read embedding job status. Please close this dialog and try again.')
      }
    }

    pollJobStatus()
    const interval = setInterval(pollJobStatus, 3000)
    return () => clearInterval(interval)
  }, [showConfirmModal, activationJobId, activationFinished, activationFailed, currentDocumentId])

  const resetActivationState = () => {
    setActivationJobId('')
    setIsActivationStarting(false)
    setIsActivationPolling(false)
    setActivationFinished(false)
    setActivationFailed(false)
    setActivationMessage('You can activate one version at a given time.')
  }

  const handleDocumentUpdate = async (id: string, isActive: boolean) => {
    console.log('[HR Activation] handleDocumentUpdate called', { id, isActive })

    if (isActive) {
      console.log('[HR Activation] Document is active, deactivating...')
      const response = await deActivateDocument(id).unwrap()
      console.log('[HR Activation] Deactivation response:', response)
      if (response) {
        handleUpdateAllIsActive(id, !isActive)
      }
    } else {
      console.log('[HR Activation] Document is inactive, activating...')
      const response = await activateDocument(id).unwrap()
      console.log('[HR Activation] Activation API response:', response)
      return response
    }

    return null
  }
  const handleToggleSwitch = (id: string, isActive: boolean) => {
    console.log('[HR Activation] handleToggleSwitch called', { id, isActive })
    setCurrentDocumentId(id)
    if (!isActive) {
      console.log('[HR Activation] Showing confirmation modal for document:', id)
      resetActivationState()
      setShowConfirmModal(true)
    } else {
      console.log('[HR Activation] Document is active, calling handleDocumentUpdate to deactivate')
      handleDocumentUpdate(id, isActive)
    }
  }
  const handleUpdateAllIsActive = (id: string, isActive: boolean) => {
    const updatedList = listHRDocumentState.map((item: any) => {
      return {
        ...item,
        isActive: item.id === id ? isActive : isActive ? false : item.isActive,
      }
    })
    dispatch(updateAllHRDocumentStatus(updatedList))
  }
  const handleApprove = async () => {
    console.log('[HR Activation] handleApprove called', { currentDocumentId, activationFinished, activationFailed })

    if (activationFinished || activationFailed) {
      console.log('[HR Activation] Already finished/failed, resetting and closing modal')
      resetActivationState()
      setShowConfirmModal(false)
      return
    }

    setIsActivationStarting(true)
    setActivationMessage('Activating selected HR list...')
    console.log('[HR Activation] Set message: Activating selected HR list...')

    try {
      console.log('[HR Activation] Calling handleDocumentUpdate with ID:', currentDocumentId)
      const response = await handleDocumentUpdate(currentDocumentId, false)
      console.log('[HR Activation] handleDocumentUpdate response:', response)

      handleUpdateAllIsActive(currentDocumentId, true)
      resetActivationState()
      setShowConfirmModal(false)
    } catch (error) {
      console.error('[HR Activation] Error during activation:', error)
      setActivationFailed(true)
      setActivationMessage('Activation failed. Please close this dialog and try again.')
    } finally {
      setIsActivationStarting(false)
    }
  }
  const handleCancel = () => {
    if (isActivationStarting || isActivationPolling) {
      return
    }

    resetActivationState()
    setShowConfirmModal(false)
  }
  const handleShowMapping = (id: string) => {
    setCurrentDocumentId(id)
    setShowMapping(true)
  }


  const handleBackgroundIndexingStarted = (documentId: string, jobId: string) => {
    // setBackgroundIndexingJobs((prev) => ({
    //   ...prev,
    //   [documentId]: jobId,
    // }))
    dispatch(setBackgroundIndexingJobs({ documentId, jobId }))
  }


  return (
    <>
      {isLoading ? (
        <MotifProgressLoader
          role="alertdialog"
          show
          variant="default"
          tip="Loading..."
        />
      ) : listHRDocumentState.length > 0 ? (
        listHRDocumentState
          .filter((item: any) => item.status)
          .map((item: any) => (
            <HRListItem
              {...item}
              key={item.id}
              isIndexing={Boolean(backgroundIndexingJobs[item.id])}
              indexingStep={indexingStepInfo[item.id]}
              isArchived={Boolean(isArchived)}
              handleToggleSwitch={handleToggleSwitch}
              showMapping={handleShowMapping}
            />
          ))
      ) : (
        <div className="no-data-found">
          {isArchived ? (
            <>
              <p className="motif-body1-default-regular">This is an archived project and is now read only</p>
              <p style={{ textAlign: 'center' }}>HR document has been deleted</p>
            </>
          ) : (
            <>
              <p className="motif-body1-default-regular">No documents found</p>
              <p style={{ textAlign: 'center' }}>
                Please upload the client's HR list to continue with the SOD review
                process for client applications
              </p>
            </>
          )}
        </div>
      )}

      <MappingModal
        isVisible={showMapping}
        setIsVisible={setShowMapping}
        documentId={currentDocumentId}
        onIndexingStarted={handleBackgroundIndexingStarted}
      />
      <ConfirmationModal
        cancelBtnHandler={handleCancel}
        confirmBtnHandler={handleApprove}
        heading="Confirm"
        message={activationMessage}
        isVisible={showConfirmModal}
        setIsVisible={setShowConfirmModal}
        loading={isActivationStarting}
        hideCancelButton={isActivationStarting || isActivationPolling || activationFinished || activationFailed}
        confirmDisabled={isActivationPolling}
        confirmText={activationFinished || activationFailed ? 'Close' : isActivationPolling ? 'Processing' : 'Confirm'}
        disableClose={isActivationStarting || isActivationPolling}
      />
    </>
  )
}
